"use client";

import {
  FC,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { usePostHog } from "posthog-js/react";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import useWindowSize from "apps/website/hooks/useWindowSize";
import useA11y from "apps/website/hooks/useA11y";
import {
  Breakpoint,
  tailwindBreakpointsMap,
} from "apps/website/maps/Breakpoint.map";
import {
  State,
  stateToBoolean,
  booleanToState,
} from "apps/website/utils/misc/state";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { IconSvg } from "apps/website/components/base/Icon/Icon.map";
import { WithTestID } from "apps/website/types";
import { useResizeObserver } from "apps/website/hooks/useResizeObserver";
import cx from "apps/website/utils/misc/cx";
import { useEvent } from "libs/hooks/src/lib/event/useEvent";
import { IUseEventDispatchEvent } from "@tracking-events/browser";

import SectionAnimatedSplit from "../../section/SectionAnimatedSplit/SectionAnimatedSplit";
import {
  Animation,
} from "../../section/SectionAnimatedSplit/SectionAnimatedSplit.map";

import {
  BreakpointState,
  AccordionType,
  ROOT_CLASS_MAP,
} from "./Accordion.map";
import styles from "./Accordion.module.css";
import { getChildrenClasses } from "./Accordion.utils";
import AccordionButton from "./AccordionButton/AccordionButton";

export interface IAccordion extends WithTestID {
  title: string;
  openTitle?: string;
  theme?: Theme;
  state?: State | BreakpointState;
  type?: AccordionType;
  Content: ReactElement;
  PreviewContent?: ReactElement;
  bottomAnimation?: Animation;
  openAccordionEvent?: IUseEventDispatchEvent<"open_accordion">;
  closeAccordionEvent?: IUseEventDispatchEvent<"close_accordion">;
  onToggle?: (isOpen: boolean) => void;
}

const Accordion: FC<IAccordion> = ({ title, theme = "default", state = "closed", type = "default", Content, PreviewContent, openTitle, bottomAnimation, "data-testid": testId, openAccordionEvent, closeAccordionEvent, onToggle }) => {
  const { windowSize, isActiveBreakpointBelow } = useWindowSize();
  const { UUID } = useA11y();
  const posthog = usePostHog();
  const isPrimingScreenTest = posthog.getFeatureFlag("priming-screen-suf-3") === "test";
  const content = useRef<HTMLDivElement>(null);

  const accordionBody = useRef<HTMLDivElement>(null);
  const [ accordionBreakpointState, setAccordionBreakpointState ] = useState<BreakpointState>({});
  const [ isAccordionOpen, setIsAccordionOpen ] = useState(false);
  const [ accordionHeight, setAccordionHeight ] = useState("0px");
  const [ headerId, setHeaderId ] = useState<string | undefined>();
  const [ bodyId, setBodyId ] = useState<string | undefined>();
  const [ icon, setIcon ] = useState<IconSvg>("minus");

  const { dispatchEvent } = useEvent();

  const setCalculatedAccordionHeight = () => {
    setAccordionHeight(`${accordionBody?.current?.offsetHeight}px`);
  };
  useResizeObserver(content, setCalculatedAccordionHeight);

  const sendAccordionEvent = (accordionEvent?: IUseEventDispatchEvent<"open_accordion" | "close_accordion">) => {
    if (!accordionEvent) { return; }
    const { name, properties } = accordionEvent;
    dispatchEvent(name, properties);
  };

  const toggleAccordion = () => {
    if (isAccordionOpen) {
      sendAccordionEvent(closeAccordionEvent);
    } else {
      sendAccordionEvent(openAccordionEvent);
    }
    onToggle?.(!isAccordionOpen);
    setAccordionBreakpointState({
      ...accordionBreakpointState,
      [windowSize?.activeBreakpoint]: booleanToState(!isAccordionOpen),
    });
  };

  const initAccordionInitialState = () => {
    const initialState: BreakpointState = {};
    let lastKey = typeof state === "string" ? state : "closed";

    for (const breakpoint in tailwindBreakpointsMap) {
      if (state || (state as BreakpointState)[breakpoint as Breakpoint] || lastKey) {
        const breakpointState = typeof state === "string"
          ? state : (state as BreakpointState)[breakpoint as Breakpoint]
            ? (state as BreakpointState)[breakpoint as Breakpoint]
            : lastKey;
        lastKey = breakpointState ?? "closed";

        initialState[breakpoint as Breakpoint] = breakpointState as State;
      }
    }

    setAccordionBreakpointState(initialState);
  };

  useEffect(() => {
    initAccordionInitialState();
    setHeaderId(`accordion-header-${UUID}`);
    setBodyId(`accordion-body-${UUID}`);
  }, [ UUID ]);

  useEffect(() => {
    setIsAccordionOpen(stateToBoolean(accordionBreakpointState[windowSize?.activeBreakpoint as Breakpoint] ?? "closed"));
  }, [ accordionBreakpointState, windowSize ]);

  useEffect(() => {
    setIcon(isAccordionOpen ? "minus" : "plus");
  }, [ isAccordionOpen ]);

  useEffect(() => {
    setCalculatedAccordionHeight();
  }, [ isAccordionOpen, windowSize ]);

  const displayTitle = (isAccordionOpen && openTitle) ? openTitle : title;

  return (
    <div
      data-testid={testId}
      data-component="Accordion"
      className={cx(
        "w-full",
        themeRootClassMap[theme],
        ROOT_CLASS_MAP[type],
        isPrimingScreenTest && "p-0",
      )}
      data-theme={theme}
      data-state={booleanToState(isAccordionOpen)}
    >
      <div className="order-last">
        <AccordionButton
          testId={testId}
          headerId={headerId}
          bodyId={bodyId}
          type={type}
          theme={theme}
          displayTitle={displayTitle}
          icon={icon}
          isAccordionOpen={isAccordionOpen}
          isPrimingScreenTest={isPrimingScreenTest}
          isActiveBreakpointBelow={isActiveBreakpointBelow}
          PreviewContent={PreviewContent}
          onToggle={toggleAccordion}
        />
      </div>
      <div className={`overflow-hidden transition-all duration-300 ${styles.body}`} style={{ "--accordion-height": accordionHeight } as React.CSSProperties}>
        <div
          id={bodyId}
          ref={accordionBody}
          aria-hidden={!isAccordionOpen}
          aria-labelledby={headerId}
          data-content
        >
          <div
            ref={content}
            data-children
            className={getChildrenClasses(type, isAccordionOpen)}
          >
            { Content }
          </div>
          <Spacer size="lg" />
          { bottomAnimation && (
            <SectionAnimatedSplit animation={bottomAnimation} themeTop={theme} themeBottom={theme}/>
          ) }
        </div>
      </div>
    </div>
  );
};

export default Accordion;
